// "Fit to box" for text boxes (customer request, 2026-08-25).
//
// Fabric's Textbox has a user-set WIDTH but its height is always the height of
// the wrapped text. Customers want to draw a field as a box with a set width
// AND height and have the value fit inside it. This module adds:
//   - `boxHeight`   : a FIXED height for the box (top/bottom handles set it).
//                     Font size / bold / content never resize the box; text
//                     that doesn't fit overflows (or shrinks, see textFit).
//   - `verticalAlign`: 'top' | 'middle' | 'bottom' — where text sits when the
//                     box is taller than the text
//   - `textFit`     : 'wrap' (default) | 'shrink' (one line, shrink to width —
//                     the old "Auto-shrink to fit width") | 'wrap-shrink'
//                     (wrap first, shrink the font only if the lines still
//                     don't fit the box) — implemented in autofit.ts
// Persisted via CANVAS_CUSTOM_PROPS; the prototype patches below make loaded
// designs (main canvas, pages, batch StaticCanvas) honour them automatically.
import { Control, Textbox, controlsUtils, type FabricObject } from 'fabric'

export type TextFit = 'wrap' | 'shrink' | 'wrap-shrink'
export type VerticalAlign = 'top' | 'middle' | 'bottom'

export type FitTextbox = Textbox & {
  boxHeight?: number
  verticalAlign?: VerticalAlign
  textFit?: TextFit
  autoFitWidth?: boolean
}

let installed = false

/** Patch Textbox once: minimum height + vertical alignment. Idempotent. */
export function installTextboxFit(): void {
  if (installed) return
  installed = true
  const proto = Textbox.prototype as unknown as {
    initDimensions: () => void
    _getTopOffset: () => number
    calcTextHeight: () => number
  }
  const origInit = proto.initDimensions
  proto.initDimensions = function (this: FitTextbox) {
    origInit.call(this)
    const bh = this.boxHeight
    if (bh) this.height = bh
  }
  const origTop = proto._getTopOffset
  proto._getTopOffset = function (this: FitTextbox) {
    const base = origTop.call(this)
    const bh = this.boxHeight
    if (!bh || !this.verticalAlign || this.verticalAlign === 'top') return base
    const free = this.height - this.calcTextHeight()
    if (free <= 0) return base
    return this.verticalAlign === 'middle' ? base + free / 2 : base + free
  }
}

/** Effective fit mode (older designs only carry `autoFitWidth`). */
export function textFitOf(o: FabricObject): TextFit {
  const t = o as FitTextbox
  if (t.textFit) return t.textFit
  return t.autoFitWidth ? 'shrink' : 'wrap'
}

/** Set the fit mode, keeping the legacy `autoFitWidth` flag in sync. */
export function setTextFit(o: FabricObject, mode: TextFit): void {
  const t = o as FitTextbox
  t.set({ textFit: mode, autoFitWidth: mode === 'shrink' } as Partial<FitTextbox>)
  // wrap-shrink needs a box to fit into: freeze the current height if none set
  if (mode === 'wrap-shrink' && !t.boxHeight) t.boxHeight = Math.round(t.height)
}

export function setBoxHeight(o: FabricObject, h: number | undefined): void {
  const t = o as FitTextbox
  t.boxHeight = h && h > 0 ? h : undefined
  t.initDimensions()
  t.setCoords()
}

export function setVerticalAlign(o: FabricObject, v: VerticalAlign): void {
  const t = o as FitTextbox
  t.set({ verticalAlign: v } as Partial<FitTextbox>)
  t.dirty = true
}

// Top/bottom-middle handles: Fabric's stock ones SCALE the object (glyphs
// squish — customer issue #8); hiding them removed vertical resizing entirely
// (customer follow-up). These set `boxHeight` instead: the box grows/shrinks
// around the opposite edge and the text never distorts.
const changeHeight = controlsUtils.changeHeight
const boxHeightAction: Control['actionHandler'] = (e, transform, x, y) => {
  const t = transform.target as FitTextbox
  const before = t.height
  const changed = changeHeight(e, transform, x, y)
  if (!changed) return false
  // Fabric wrote the pointer height into `height`; make it the fixed box height.
  void before
  t.boxHeight = Math.max(4, Math.round(t.height))
  t.initDimensions()
  t.setCoords()
  return true
}

/** Give a Textbox height handles that set `boxHeight` (call on object:added). */
export function applyBoxHeightControls(o: FabricObject): void {
  if ((o.type ?? '').toLowerCase() !== 'textbox') return
  const t = o as FitTextbox
  const mk = (y: number) => new Control({
    x: 0, y, actionHandler: boxHeightAction, cursorStyleHandler: controlsUtils.scaleSkewCursorStyleHandler, actionName: 'resizing',
  })
  t.controls = { ...t.controls, mt: mk(-0.5), mb: mk(0.5) }
  t.setControlsVisibility({ mt: true, mb: true })
}
